import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { orderWindowsKeyFactory } from "@features/orderWindows";
import { DemandPlan, DemandPlanSummary } from "@models/DemandPlan";
import client, { RequestParams, useMutateError } from "@services/api";
import { camelCaseKeys } from "@utility/utilityFunctions";

import { demandPlanVariantQueryKeyFactory } from "./demandPlanVariantQueries";

const apiRoot = "/demand-plans";

export const demandPlanQueryKeyFactory = createQueryKeys("demandPlans", {
  detail: (id: string) => ({
    queryKey: [id],
    queryFn: () =>
      client.get<DemandPlan>(`${apiRoot}/${id}`).then((res) => res.data),
    contextQueries: {
      summary: {
        queryKey: null,
        queryFn: () =>
          client
            .get(`${apiRoot}/${id}/summary`, {
              deserializeOutput: false,
            })
            .then((res) => camelCaseKeys(res) as DemandPlanSummary),
      },
    },
  }),
  list: (params) => ({
    queryKey: [params],
    queryFn: () =>
      client
        .get<DemandPlan[]>(apiRoot, {
          params,
        })
        .then((res) => res.data),
  }),
});

export const useDemandPlanQuery = (id: string | null | undefined) => {
  return useQuery({
    ...demandPlanQueryKeyFactory.detail(id!),
    enabled: !!id,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
};

export const useDemandPlanListQuery = (params?: RequestParams) => {
  return useQuery({
    ...demandPlanQueryKeyFactory.list({
      skipPagination: true,
      ...params,
    }),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
};

type DemandPlanPayload = {
  name: string;
  orderWindows: number[];
  expectedPurchaseOrderDate: Date;
};

type UpdatePayload = {
  id: string;
};
type UpdateDemandPlanPayload = UpdatePayload & DemandPlanPayload;

type UpdateDemandPlanActivePayload = UpdatePayload & {
  isActive: boolean;
};

export const useCreateDemandPlanMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: DemandPlanPayload) =>
      client
        .post<DemandPlan>(apiRoot, {
          __type: "demand-plan",
          ...payload,
          relationshipNames: ["orderWindows"],
        })
        .then((res) => res.data),
    onError: (error) => setError(error, "Create Demand Plan"),
    onSuccess: (dp) => {
      queryClient.invalidateQueries({
        queryKey: demandPlanQueryKeyFactory.list._def,
      });

      // Order windows are associated with demand plans, refresh order windows when demand plan is created
      queryClient.invalidateQueries({
        queryKey: orderWindowsKeyFactory.list._def,
      });

      return queryClient.setQueryData(
        demandPlanQueryKeyFactory.detail(dp.id).queryKey,
        dp
      );
    },
  });
};

export const useUpdateDemandPlanMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: UpdateDemandPlanPayload | UpdateDemandPlanActivePayload) =>
      client
        .put<DemandPlan>(`${apiRoot}/${id}`, {
          __type: "demand-plan",
          ...payload,
          relationshipNames: ["orderWindows"],
        })
        .then((res) => res.data),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: demandPlanQueryKeyFactory.list._def,
      });

      // Order windows are associated with demand plans, refresh order windows when demand plan is updated
      queryClient.invalidateQueries({
        queryKey: orderWindowsKeyFactory.list._def,
      });

      // If a demand plan changes, make sure to reload the variants
      queryClient.invalidateQueries({
        queryKey: demandPlanVariantQueryKeyFactory.list._def,
      });
      queryClient.invalidateQueries({
        queryKey: demandPlanVariantQueryKeyFactory.paginated._def,
      });

      return queryClient.setQueryData(
        demandPlanQueryKeyFactory.detail(id).queryKey,
        data
      );
    },
    onError: (error) => {
      setError(error, "Update Demand Plan");
    },
  });
};

export const useDemandPlanSummaryQuery = (id: string | null | undefined) => {
  return useQuery({
    ...demandPlanQueryKeyFactory.detail(id!)._ctx.summary,
    enabled: !!id,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
};
